import React from 'react';
import { toast, Id, ToastOptions } from 'react-toastify';
import { dispatch } from './helpers';
import { toaster, createDefaultOptions, ToastType, NotificationMessage } from '@utils/notification';
import { AxiosError } from 'axios';
import { routesPath } from 'src/router/routes';
import { call } from 'redux-saga/effects';
import _ from 'lodash';

const toastTitles: Record<ToastType, string> = {
  success: 'Successful',
  error: 'Error',
  info: 'Info',
  warning: 'Warning',
  readyDownload: 'Ready to download',
};

export function runToast(
  type: ToastType,
  message: string,
  title?: string,
  options?: ToastOptions<object>
) {
  const defaultOptions = createDefaultOptions(type);
  const combineOptions = options ? { ...defaultOptions, ...options } : defaultOptions;
  toaster[type](
    { title: title ?? toastTitles[type], text: message, type },
    combineOptions
  );
}

export function updateRunToast(
  id: Id,
  type: ToastType,
  message: string,
  title?: string,
  options?: ToastOptions<object>
) {
  const defaultOptions = createDefaultOptions(type);
  const combineOptions = options ? { ...defaultOptions, ...options } : defaultOptions;
  toast.update(id, {
    render: React.createElement(NotificationMessage, {
      title: title ?? toastTitles[type],
      text: message,
      type,
      toastProps: combineOptions,
    }),
    ...combineOptions,
  });
}

export const GO_TO_ROUTE_ACTION = 'SAGA_ROUTER/GO_TO_ROUTE';

export function goToRoute(path: string) {
  return dispatch({
    type: GO_TO_ROUTE_ACTION,
    payload: `${path}?${new URLSearchParams(window.location.search).toString()}`,
  });
}

export function* redirectToCTListOnError(error: unknown) {
  if ((error as AxiosError).response?.status === 403) {
    yield goToRoute(routesPath.qc);
  }
}

export function* showErrorToast(error: unknown, message: string) {
  if (error instanceof AxiosError && error.response?.data?.detail) {
    yield call(runToast, 'error', error.response.data.detail);
  } else {
    yield call(runToast, 'error', message);
  }
}

const adjectives = [
  'crazy',
  'happy',
  'sad',
  'angry',
  'excited',
  'bored',
  'lazy',
  'brave',
  'calm',
  'eager',
];
const animals = [
  'cat',
  'dog',
  'elephant',
  'tiger',
  'lion',
  'giraffe',
  'zebra',
  'monkey',
  'bear',
  'wolf',
];

const isLocalhost = /localhost/.test(window.location.hostname);

export const getRandomUsername = () => {
  const adj = _.sample(adjectives);
  const animal = _.sample(animals);
  return `${adj}-${animal}`;
};

export const getUniqTabUUID = () => {
  return sessionStorage.getItem('TAB_ID_KEY');
};

(function () {
  function generateId() {
    const longUuid = generateSecureUuid();
    const short = longUuid.split('-')[0];
    const id = isLocalhost ? getRandomUsername() + '-' + short : longUuid;
    return id;
  }

  function generateSecureUuid(prefix = 'test-fallback-uuid-') {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return prefix + crypto.randomUUID();
    }
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.getRandomValues === 'function'
    ) {
      const array = new Uint8Array(16);
      crypto.getRandomValues(array);
      const hex = Array.from(array, byte =>
        byte.toString(16).padStart(2, '0')
      ).join('');
      return prefix + hex;
    }
    throw new Error(
      'Secure random number generation is not supported in this environment'
    );
  }

  const navEntries =
    typeof performance !== 'undefined' &&
    typeof performance.getEntriesByType === 'function'
      ? performance.getEntriesByType('navigation')
      : [];
  const navType =
    navEntries.length > 0
      ? (navEntries[0] as PerformanceNavigationTiming).type
      : 'navigate';

  if (navType === 'reload') {
    if (!sessionStorage.getItem('TAB_ID_KEY')) {
      sessionStorage.setItem('TAB_ID_KEY', generateId());
    }
  } else {
    sessionStorage.setItem('TAB_ID_KEY', generateId());
  }
})();
